import React, { useEffect, useMemo, useState } from 'react';
import useSharedScheduleRealtime from '../../realtime/useSharedScheduleRealtime';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { Alert, Button } from 'react-bootstrap';
import TopNav from '../TopNav/TopNav';
import GlobalJobSearch from '../JobSchedulePage/components/GlobalJobSearch';
import useDebounce from '../JobSchedulePage/hooks/useDebounce';
import { clearJobSchedule, fetchJobSchedule } from '../../redux/slices/jobScheduleSlice';
import { filterJobsForSchedule } from '../JobSchedulePage/utils/globalSearchUtils';
import { usePermissions } from '../../hooks/usePermissions';
import ControlsSchedule from './ControlsSchedule';
import ControlsCalendar from './ControlsCalendar';
import { getControlsWorkspaceJobs } from './utils/controlsScheduleData';
import './controls.css';

const CONTROLS_TAB_CONFIG = [
  { id: 'controlsSchedule', label: 'Controls Schedule', permissionKey: 'controls.schedule' },
  { id: 'controlsCalendar', label: 'Controls Calendar', permissionKey: 'controls.calendar' },
];

const ControlsContainer = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('controlsSchedule');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const jobScheduleData = useSelector((state) => state.jobSchedule.data);
  const loading = useSelector((state) => state.jobSchedule.loading);
  const error = useSelector((state) => state.jobSchedule.error);
  const { canView, canEdit, isLoading: isPermissionsLoading } = usePermissions();

  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const hasContainerAccess = canView('controls.container');
  useSharedScheduleRealtime({ enabled: hasContainerAccess, blocked: loading || isRefreshing || isPermissionsLoading });

  useEffect(() => {
    dispatch(clearJobSchedule());
    dispatch(fetchJobSchedule());
  }, [dispatch]);

  const visibleTabs = useMemo(
    () => CONTROLS_TAB_CONFIG.filter((tab) => canView(tab.permissionKey)),
    [canView]
  );

  const filteredJobs = useMemo(() => {
    const scheduleJobs = filterJobsForSchedule(jobScheduleData, debouncedSearchTerm);
    return getControlsWorkspaceJobs(scheduleJobs);
  }, [jobScheduleData, debouncedSearchTerm]);

  const canEditAnyVisibleTab = useMemo(
    () => visibleTabs.some((tab) => canEdit(tab.permissionKey)),
    [canEdit, visibleTabs]
  );

  useEffect(() => {
    if (isPermissionsLoading) {
      return;
    }

    if (visibleTabs.length === 0) {
      if (activeTab !== '') {
        setActiveTab('');
      }
      return;
    }

    if (!visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [activeTab, isPermissionsLoading, visibleTabs]);

  const handleRefresh = async () => {
    setIsRefreshing(true);

    try {
      dispatch(clearJobSchedule());
      await dispatch(fetchJobSchedule()).unwrap();
    } catch (refreshError) {
      console.error('Error refreshing controls schedule data:', refreshError);
    } finally {
      setIsRefreshing(false);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'controlsCalendar':
        return <ControlsCalendar filteredJobs={filteredJobs} canEdit={canEdit('controls.calendar')} />;
      case 'controlsSchedule':
        return <ControlsSchedule filteredJobs={filteredJobs} canEdit={canEdit('controls.schedule')} />;
      default:
        return null;
    }
  };

  if (isPermissionsLoading) {
    return (
      <div>
        <TopNav />
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading controls permissions...</p>
        </div>
      </div>
    );
  }

  if (!hasContainerAccess) {
    return <Navigate to="/" replace />;
  }

  return (
    <div>
      <TopNav />
      {!canEditAnyVisibleTab && visibleTabs.length > 0 && (
        <Alert variant="info" className="mx-3 mt-3">
          You are viewing the controls workspace in read-only mode based on your component policy permissions.
        </Alert>
      )}

      {visibleTabs.length === 0 ? (
        <div className="m-3">
          <Alert variant="warning">
            You do not currently have access to any controls tabs. Contact an administrator to update your component policies.
          </Alert>
        </div>
      ) : (
        <>
          <div className="mx-3 mb-3 d-flex align-items-center gap-3 mt-3">
            <div className="flex-grow-1">
              <GlobalJobSearch
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                placeholder="Search controls jobs by number, name, or model"
              />
            </div>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing || loading}
              className="d-flex align-items-center"
            >
              {isRefreshing ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Refreshing...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-repeat p-1"></i>
                </>
              )}
            </Button>
          </div>
          <div className="m-3">
            <ul className="nav nav-tabs mb-4">
              {visibleTabs.map((tab) => (
                <li className="nav-item" key={tab.id}>
                  <button
                    className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>

            {loading ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading controls jobs...</p>
              </div>
            ) : error ? (
              <div className="alert alert-danger">Error: {error}</div>
            ) : (
              <div className="tab-content">{renderContent()}</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ControlsContainer;
